import { useState } from 'react';
import { findFirstPartyMcpForConnection } from '@wix/ai-scheduling-assistant-constants';
import { Spinner } from '../../Spinner';
import { mcpStatusLabel, type McpConnectionSummary } from '../../../lib/mcp';
import { CONNECTORS_PAGE, mcpSectionShowsConnection, useMcpController } from '../../../lib/use-connectors';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useTranslation } from '../../../lib/i18n';
import { LogoFallback } from './ConnectorLogo';
import { CardGridSkeleton, ErrorLine, Section } from './primitives';
import { ChevronIcon, PlusIcon } from './icons';
import { matchesMcpQuery } from './matching';
import styles from './connectors.module.css';
import { Button } from '../ui';
import { Modal } from '../../Modal';

type Translate = (k: string, o?: Record<string, unknown>) => string;

function mcpToolsLabel(connection: McpConnectionSummary, t: Translate): string | undefined {
  if (connection.toolCount === undefined) return undefined;
  const unit = connection.toolCount === 1 ? t('connectors.mcp.toolOne') : t('connectors.mcp.toolOther');
  return `${connection.toolCount} ${unit}`;
}

export function McpSection({
  query,
  bottomCta,
  rowedSlugs,
}: {
  query: string;
  bottomCta: { route: string; path: string };
  /** Registry slugs the apps section already renders a row for, or `null` while that is still loading —
   *  see `connectorRowSlugs`. */
  rowedSlugs: Set<string> | null;
}) {
  const { t } = useTranslation();
  const { logPageClickBi, logModalActionBi } = useGenericBi();
  const { state, busy, actionError, remove, logConnectClick } = useMcpController();
  // Component-level load (198:4/5) for the MCP-connections fetch — a real async region
  // with no existing load coverage (the apps list is covered by the page-view in
  // useAppsController). Click logs for connect_mcp / disconnect_mcp already live in the controller.
  useLoadBi('mcp-list', { isLoading: state.kind === 'loading', isError: state.kind === 'error' });
  const [detail, setDetail] = useState<McpConnectionSummary | null>(null);
  const searching = query.length > 0;
  const serverFallback: string = t('connectors.mcp.serverFallback');
  // A registry server belongs to its connector row in the apps section — but only while that row is
  // actually rendered. When it is not (a failed read, or a connector whose rollout closed), the
  // connection shows up here so it is never left without a disconnect.
  const owned =
    state.kind === 'ready' ? state.connections.filter((c) => mcpSectionShowsConnection(findFirstPartyMcpForConnection(c), rowedSlugs)) : [];
  const filtered = searching ? owned.filter((c) => matchesMcpQuery(c.name || c.url || serverFallback, c.url, query)) : owned;

  if (searching && state.kind === 'ready' && filtered.length === 0) return null;

  return (
    <Section title={t('connectors.mcp.title')} count={state.kind === 'ready' ? filtered.length : undefined}>
      {state.kind === 'loading' && <CardGridSkeleton count={2} />}
      {state.kind === 'error' && <ErrorLine message={t('connections.error')} />}
      {state.kind === 'ready' && (
        <>
          {actionError && <ErrorLine message={actionError} />}
          {filtered.length === 0 ? (
            <div className={`${styles.card} ${styles.empty}`}>{t('connectors.mcp.emptySymphony')}</div>
          ) : (
            <div className={styles.mcpGrid}>
              {filtered.map((c) => (
                <McpCard
                  key={c.id}
                  connection={c}
                  onOpen={(connection) => {
                    logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'open_mcp' });
                    setDetail(connection);
                  }}
                />
              ))}
            </div>
          )}
          {!searching && (
            <div className={styles.mcpCtaBottom}>
              <Button
                href={bottomCta.route}
                linkAs={bottomCta.path}
                size="large"
                fullWidth
                startIcon={<PlusIcon size={16} />}
                onClick={logConnectClick}
              >
                {t('connectors.mcp.connect')}
              </Button>
            </div>
          )}
          {detail && (
            <McpDetailModal
              connection={detail}
              busy={busy}
              onRemove={(id, name) => {
                // remove() already logs the disconnect_mcp click in useMcpController — no modal action here to avoid double-instrumenting.
                remove(id, name);
                setDetail(null);
              }}
              onClose={() => {
                logModalActionBi({ ...CONNECTORS_PAGE, modal_name: 'mcp_detail', action: 'close' });
                setDetail(null);
              }}
            />
          )}
        </>
      )}
    </Section>
  );
}

function McpCard({ connection, onOpen }: { connection: McpConnectionSummary; onOpen: (c: McpConnectionSummary) => void }) {
  const { t } = useTranslation();
  const serverFallback: string = t('connectors.mcp.serverFallback');
  const name = connection.name || connection.url || serverFallback;
  const { label, healthy } = mcpStatusLabel(connection.status, connection.lastError);
  const tools = mcpToolsLabel(connection, t);
  return (
    <button
      type="button"
      className={`${styles.card} ${styles.cardTappable}`}
      data-connected={healthy || undefined}
      onClick={() => onOpen(connection)}
    >
      <div className={styles.main}>
        <LogoFallback letter={name.charAt(0)} size={40} />
        <div className={styles.text}>
          <div className={`sym-h4 ${styles.name} ${styles.nameEllipsis}`}>{name}</div>
          <div className={`${styles.desc} ${styles.mcpStatus}`}>
            <span
              aria-hidden
              className={styles.mcpDot}
              style={{ background: healthy ? 'var(--accent-solid)' : 'rgba(var(--ds-fill-destructive-hover-rgb), 0.85)' }}
            />
            <span className={styles.mcpStatusText}>
              {label}
              {tools ? ` · ${tools}` : ''}
            </span>
          </div>
        </div>
        <div className={styles.aside}>
          <ChevronIcon />
        </div>
      </div>
    </button>
  );
}

function McpDetailModal({
  connection,
  busy,
  onRemove,
  onClose,
}: {
  connection: McpConnectionSummary;
  busy: ReadonlySet<string>;
  onRemove: (id: string, name: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const name = connection.name || connection.url || t('connectors.mcp.serverFallback') || '';
  const { label, healthy } = mcpStatusLabel(connection.status, connection.lastError);
  const tools = mcpToolsLabel(connection, t);
  const isBusy = busy.has(connection.id);
  return (
    <Modal open onClose={onClose} width={420} maxHeightPx={650} scrollBody labelledBy="mcp-detail-title">
      <div className={styles.modalHead}>
        <LogoFallback letter={name.charAt(0)} size={40} />
        <div className={styles.text}>
          <div id="mcp-detail-title" className={`sym-h4 ${styles.name}`}>
            {name}
          </div>
          <div className={`${styles.desc} ${styles.mcpStatus}`}>
            <span
              aria-hidden
              className={styles.mcpDot}
              style={{ background: healthy ? 'var(--accent-solid)' : 'rgba(var(--ds-fill-destructive-hover-rgb), 0.85)' }}
            />
            {label}
            {tools ? ` · ${tools}` : ''}
          </div>
        </div>
      </div>
      {connection.status === 'CONNECTED' && connection.lastError && <div className={styles.desc}>{connection.lastError}</div>}
      {connection.url && <div className={styles.modalUrl}>{connection.url}</div>}
      <Button
        variant="text"
        color="destructive"
        fullWidth
        onClick={() => onRemove(connection.id, name)}
        disabled={isBusy}
        loading={isBusy}
        aria-label={t('connectors.mcp.removeNamed', { name })}
        style={{ marginTop: 4 }}
      >
        {t('common.disconnect')}
      </Button>
    </Modal>
  );
}
